import {
  DETECT_IMAGERY,
  DETECT_IMAGERY_FAILURE,
  DETECT_IMAGERY_LOADING,
  DETECT_IMAGERY_SUCCESS,
  storeAvailableImagery,
} from 'actions/designer'
import lodash from 'lodash'
import { imagerySelectors } from 'reducer/designer/detectImagery'
import { call, put, select, takeLatest } from 'redux-saga/effects'
import { DefaultImageryStrategy } from 'types/imagery'
import { MapDataTypes } from 'types/map'
import { Location4326 } from 'types/studio/structs'
import { attachCoverageWalletProductsToMapTypes } from 'util/coverageWalletProducts'

export interface DetectImagerySideEffect {
  payload: {
    location4326: any
    country_iso2: string
    state: string
    address: string
    premiumImgIsAvailable: boolean
    is_lite: boolean
    onComplete?: (res: { availableMapTypes: any; timezoneOffset: number } | undefined, e: any) => void
    enablePreload: boolean
  }
}

export const getMapTypesAtLocation = async (
  location4326: Location4326,
  country_iso2: string,
  state: string,
  address: string,
  is_lite: boolean,
  omitMapTypes: string[],
  default_imagery_strategy: DefaultImageryStrategy,
  enablePreload: boolean
) => {
  const includeParts: string[] = []
  if (default_imagery_strategy === 'optimize-for-auto-design') {
    includeParts.push('parcel_services')
    includeParts.push('coverage_quality')
  }
  const includePartsStr = includeParts.join(',')

  const queryParams: Record<string, any> = {}
  if (includePartsStr) queryParams.include = includePartsStr
  if (is_lite) queryParams.enable_other_imagery = 0
  if (enablePreload) queryParams.preload = 'gsa'
  let ret
  // first check to see if PAYG premium imagery is available, the result of this call can dictate how we interact with the cached imagery results
  try {
    let premiumImgResult = window.AccountHelper.getIsPremiumImageryAvailable(location4326, country_iso2, state)
    ret = await doGetMapTypesAtLocation(
      location4326,
      country_iso2,
      state,
      address,
      premiumImgResult?.isAvailable,
      queryParams
    )
  } catch {
    ret = await doGetMapTypesAtLocation(location4326, country_iso2, state, address, false, queryParams)
  }

  if (is_lite) {
    const isNearmap = !!window.getStorage().getItem('nearmap_token')

    // Allow Google Top for NM Lite users only if not 2D NM imagery is available
    const allowGoogleTop =
      !isNearmap ||
      !ret[0].find(
        (mapType) =>
          mapType.provider === 'Nearmap' && (mapType.provider === 'Nearmap' || mapType.provider === 'NearmapSource')
      )

    ret[0] = ret[0].filter((mapType) => {
      if (mapType.provider === 'Google') {
        switch (mapType.map_type) {
          case 'GoogleTop':
            return allowGoogleTop
          case 'Google':
            return !isNearmap
          case 'GoogleRoadMap':
            return true
          default:
            return false
        }
      }
      if (mapType.provider === 'MetroMap') return false
      if (mapType.provider === 'Image') return !isNearmap
      if (mapType.design_mode === '3D') return false
      else return true
    })
  }

  if (omitMapTypes?.length > 0) {
    ret[0] = ret[0].filter((mapType) => !omitMapTypes.includes(mapType.map_type))
  }

  return ret
}

const doGetMapTypesAtLocation = async (
  location4326: Location4326,
  country_iso2: string,
  state: string,
  address: string,
  premium_img_available: boolean,
  queryParams: Record<string, any>
) => {
  // @TODO: Ensure cache is cleared at the appropriate times, including:
  //  On change of project
  //  On logout/login of OpenSolar
  //  On change of Nearmap Token (which could theoretically happen wihtout logout/login of OpenSolar, in future)
  //
  // For now, we simply avoid caching any result which includes warning matching "LOGIN_PROMPT".
  var availableMapTypes: MapDataTypes[] = []

  var identifier = 'cachedGetMapTypesAtLocationRequest'
  var cachedGetMapTypesAtLocationRequest = window.AccountHelper.terrainUrlsCache.get(
    identifier,
    location4326,
    country_iso2,
    state,
    premium_img_available
  )
  if (cachedGetMapTypesAtLocationRequest) {
    return cachedGetMapTypesAtLocationRequest
  }

  var fetchPromise

  // Cached data not found, now try to check for an unresolved promise, if we find it then listen to it
  var identifierPromise = 'cachedGetMapTypesAtLocationRequestPromise'
  var cachedGetMapTypesAtLocationRequestPromise = window.AccountHelper.terrainUrlsCache.get(
    identifierPromise,
    location4326,
    country_iso2,
    state,
    premium_img_available
  )
  if (cachedGetMapTypesAtLocationRequestPromise) {
    if (window.studioDebug) {
      console.log('Using cached cachedGetMapTypesAtLocationRequestPromise')
    }
    fetchPromise = cachedGetMapTypesAtLocationRequestPromise
  } else {
    var url =
      window.API_BASE_URL +
      'orgs/' +
      window.getStorage().getItem('org_id') +
      '/maps/check_coverage/?lon=' +
      location4326[0] +
      '&lat=' +
      location4326[1] +
      '&country_iso2=' +
      country_iso2 +
      '&state=' +
      state +
      '&address=' +
      address

    url += lodash.keys(queryParams).reduce((res, key) => {
      return `${res}&${key}=${queryParams[key]}`
    }, '')

    if (window.getStorage().getItem('sample_imagery') === '1') {
      url += '&sample=1'
    }

    var nearmap_token = window.getStorage().getItem('nearmap_token')
    if (nearmap_token) {
      url += '&nearmap_token=' + nearmap_token
    }

    // url += '&sample=1'
    // url += '&sample=1_small'

    fetchPromise = fetch(url, {
      headers: window.Utils.tokenAuthHeaders(),
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
    })

    // save the promise so other call can use it
    window.AccountHelper.terrainUrlsCache.save(
      identifierPromise,
      location4326,
      country_iso2,
      state,
      premium_img_available,
      fetchPromise
    )
  }

  var timezoneOffset

  await fetchPromise
    // We must clone the response if we want to be able to read the stream multiple times
    // This only happens if we make a second call while the first is in transit. Future calls after the first
    // have already returned will hit the data cache and will not required processing the stream again
    .then((response) => response.clone().json())
    .then((data) => {
      if (window.studioDebug) {
        console.log('Success:', data)
      }
      availableMapTypes = data.available_map_types
      timezoneOffset = data.timezone_offset
      attachCoverageWalletProductsToMapTypes(availableMapTypes, data.wallet_products)

      // Register any mapTypes which are constructed dynamically
      availableMapTypes.forEach((availableMapType) => {
        if (!window.MAP_TYPES[availableMapType.map_type]) {
          if (
            availableMapType.variation_data?.class &&
            window.MAP_TYPES_CONSTRUCTORS[availableMapType.variation_data?.class]
          ) {
            window.MAP_TYPES_CONSTRUCTORS[availableMapType.variation_data?.class](
              availableMapType.map_type,
              availableMapType.variation_data
            )
          } else {
            throw new Error('Map type not recognized: ' + availableMapType.map_type)
          }
        }
      })

      // API Key has different errors to NMOS.
      // If API is invalid then show a simple message.
      // If NMOS is invalid them show login prompt
      // We CANNOT just check that they have NMOS here because they may have lost their token somehow, so we do this
      // the other way - if we find a nearmap token we assume they are NOT NMOS.
      var has_nearmap_login_warning =
        data.warnings && data.warnings.some((warning) => warning.indexOf('LOGIN_PROMPT') !== -1)

      if (has_nearmap_login_warning) {
        if (window.AccountHelper.hasNearmapApiKey()) {
          // Warning is disabled for API Key because we may get these error messages in situations where we should not
          // show an error. e.g. Perhaps their API Key only offers Vertical imagery, Source photos chould show an error.
          // Designer.showNotification(window.translate('Nearmap imagery not found. Please contact support.'), 'danger')
        } else {
          window.editor.signals.requestNearmapLogin.dispatch()
          // window.Designer?.setUiState('LoginWithNearmapDialog', {
          //   isOpen: true,
          // })
          // if (window.Designer && window.Designer.uiRefs && window.Designer.uiRefs['LoginWithNearmapDialog']) {
          //   window.Designer.uiRefs['LoginWithNearmapDialog'].setState({
          //     isOpen: true,
          //   })
          // }
        }
      } else {
        // Only save if no LOGIN_PROMPT warning received
        window.AccountHelper.terrainUrlsCache.save(
          identifier,
          location4326,
          country_iso2,
          state,
          premium_img_available,
          [availableMapTypes, timezoneOffset]
        )
      }
    })
    .catch((error) => {
      console.error('Error:', error)
    })
    .finally(() => {
      window.AccountHelper.terrainUrlsCache.delete(
        identifierPromise,
        location4326,
        country_iso2,
        state,
        premium_img_available
      )
    })

  return [availableMapTypes, timezoneOffset]
}

var identifier = 'cachedGetMapTypesAtLocationRequest'

export const getDetectImageryCacheKey = (
  location4326: Location4326,
  country_iso2: string,
  state: string,
  premium_img_available: boolean
) => {
  return window.AccountHelper.terrainUrlsCache.key(identifier, location4326, country_iso2, state, premium_img_available)
}

export function* handleDetectImagery({ payload }: DetectImagerySideEffect) {
  const { location4326, country_iso2, state, address, premiumImgIsAvailable, is_lite, onComplete, enablePreload } =
    payload || {}

  try {
    if (location4326[0] && location4326[1]) {
      yield put({
        type: DETECT_IMAGERY_LOADING,
        payload: {
          cacheKey: window.AccountHelper.terrainUrlsCache.key(
            identifier,
            location4326,
            country_iso2,
            state,
            premiumImgIsAvailable
          ),
        },
      })

      const blockedMapTypes = yield select(imagerySelectors.getBlockedMapTypes)
      let default_imagery_strategy = yield select(imagerySelectors.getDefaultImageryStrategy)

      const response = yield call(
        getMapTypesAtLocation,
        location4326,
        country_iso2,
        state,
        address,
        is_lite,
        blockedMapTypes,
        default_imagery_strategy,
        enablePreload
      )

      var availableMapTypes, timezoneOffset

      if (!response) {
        throw new Error('No response from call!')
      }

      // @TODO: Why is the response format sometimes different? We handle both versions here
      // since we have not standardized them yet.
      if (response.payload) {
        availableMapTypes = response.payload.availableMapTypes
        timezoneOffset = response.payload.timezoneOffset
      } else {
        availableMapTypes = response[0]
        timezoneOffset = response[1]
      }

      if (!availableMapTypes || availableMapTypes.length === 0) {
        throw new Error('No imagery found')
      }

      storeAvailableImagery(availableMapTypes, timezoneOffset)

      yield put({
        type: DETECT_IMAGERY_SUCCESS,
        payload: {
          availableMapTypes: availableMapTypes,
          timezoneOffset: timezoneOffset,
        },
      })
    }
    if (onComplete)
      onComplete(
        {
          availableMapTypes: availableMapTypes,
          timezoneOffset: timezoneOffset,
        },
        undefined
      )
  } catch (e) {
    console.error(e)
    yield put({ type: DETECT_IMAGERY_FAILURE, payload: { detail: String(e) } })
    if (onComplete) onComplete(undefined, e)
  }
}

export function* watchDetectImagery() {
  //@ts-ignore
  yield takeLatest(DETECT_IMAGERY, handleDetectImagery)
}
